import clsx from 'clsx'
import { type ReactNode, useMemo, useState } from 'react'

import {
  ClipboardButton,
  FvButton,
  FvLinkButton,
  ValidatedForm,
} from '@fv/client-components'
import { getTrackingNumLabel } from '@fv/client-core'
import type { FullShipment } from '@fv/client-types'

import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { Pinline } from '@/components/Pinline'
import {
  EditableRefNum,
  NOT_AVAILABLE,
  ReadonlyRefNum,
} from '@/components/shared/EditableRefNum'
import { refNumsByType } from '@/utils/shipmentFuncs'

import { usePermissions } from '../auth'
import { BolNumAvailabilityAdornment } from '../bol/BolNumAvailabilityAdornment'
import { useEditBolNumber } from '../bol/useEditBolNumber'
import { useEditConfirmationNumber } from '../confirm/useEditConfirmationNumber'
import {
  useEditableRefNums,
  useEditSealNumber,
  useEditTrailerNumber,
} from '../track/useEditableRefNums'
import { useEditTrackingNumber } from '../track/useEditTrackingNumber'

type Props = {
  load: FullShipment
  wrap?: (children: ReactNode) => ReactNode
}

export const RefNumsEditor = ({ load, wrap }: Props) => {
  const { bol, loadId, pickup, refNums, tracking, workflow, selectedQuote } =
    load

  const numsByType = useMemo(() => refNumsByType(refNums) ?? {}, [refNums])

  const sealNum = numsByType['seal-num']
  const trailerNum = numsByType['trailer-num']
  const tractorNum = numsByType['tractor-num']
  const loadNum = numsByType['load-num']

  const genericRefNums = refNums?.filter(n => n.type === 'other')
  const parsedWrap = wrap || (children => children)

  const editBOLNum = useEditBolNumber(loadId)
  const editPickupNum = useEditConfirmationNumber(loadId)
  const editTrackingNum = useEditTrackingNumber(loadId)
  const [editingRefNumbers, setEditingRefNums] = useState(false)

  function toggleEditingRefNums() {
    if (canEditRefNums) setEditingRefNums(!editingRefNumbers)
  }

  const editTrailerNum = useEditTrailerNumber(loadId, refNums)
  const editSealNum = useEditSealNumber(loadId, refNums)
  const refNumString = genericRefNums?.map(n => n.value).join(' / ')

  const { canBook } = usePermissions()
  const {
    editableRefNums,
    canEditRefNums,
    onRefNumChange,
    onRefNumSave,
    isLoading: refNumsLoading,
  } = useEditableRefNums(load)

  return (
    <>
      {Boolean(load.workflow === 'truckload') &&
        parsedWrap(
          <div className="flex items-center gap-x-2">
            <FvLinkButton
              theme="underlined"
              onClick={toggleEditingRefNums}
              className={clsx(
                'break-all !whitespace-normal b1350:!whitespace-nowrap',
                { disabled: !canEditRefNums },
              )}
            >
              Ref# {refNumString || NOT_AVAILABLE}
            </FvLinkButton>
            {refNumString && <ClipboardButton value={refNumString} />}
          </div>,
        )}
      {canEditRefNums &&
        editingRefNumbers &&
        parsedWrap(
          <>
            <ValidatedForm
              className="b1350:flex b1350:items-center"
              onValidSubmit={() => {
                onRefNumSave({
                  onSuccess: () => setEditingRefNums(false),
                })
              }}
            >
              {editableRefNums.map((n, i) => {
                return (
                  <div
                    className="mb-2 mt-2 flex flex-col space-y-2 b1350:m-0 b1350:mr-2"
                    key={`ref-num-${i}`}
                  >
                    <InputGroup
                      className="b1350:w-80 mb-0"
                      inputType="text"
                      inputProps={{
                        required: true,
                        minLength: 1,
                        name: `ref-num-${i}`,
                        onChange: e => onRefNumChange(e, i),
                        readOnly: refNumsLoading,
                        value: n.value,
                      }}
                      startContent={
                        <InputAdornment
                          position="start"
                          className="whitespace-nowrap"
                        >
                          Ref# {i + 1}
                        </InputAdornment>
                      }
                    />
                  </div>
                )
              })}
              <div className="flex items-center">
                <Pinline dotted className="flex-1 mr-2 !border-fv-blue" />
                <FvButton
                  icon="check"
                  theme="white"
                  loading={refNumsLoading}
                  type="submit"
                >
                  Save
                </FvButton>
                <FvButton
                  onClick={() => setEditingRefNums(false)}
                  icon="times"
                  theme="plain"
                  className="pr-0 m-0"
                  iconClass="text-fv-orange-bright"
                />
              </div>
            </ValidatedForm>
            <Pinline rule className="b1350:hidden" />
          </>,
        )}
      {parsedWrap(
        <EditableRefNum
          defaultValue={tracking?.trackingNumber}
          label={getTrackingNumLabel(workflow, selectedQuote)}
          loading={editTrackingNum.isPending}
          name="trackingNum"
          onSave={editTrackingNum.mutate}
          readOnly={!canBook}
        />,
      )}
      {tracking?.assetCarrierTrackingNumber &&
        parsedWrap(
          <EditableRefNum
            defaultValue={tracking?.assetCarrierTrackingNumber}
            label="Carrier PRO#"
            loading={false}
            name="carrierTrackingNum"
            readOnly
          />,
        )}
      {parsedWrap(
        <EditableRefNum
          defaultValue={pickup?.confirmationNumber}
          label={`${workflow === 'truckload' ? '' : 'Pickup '}Confirmation#`}
          loading={editPickupNum.isPending}
          name="pickupNum"
          onSave={editPickupNum.mutate}
          readOnly={!canBook}
        />,
      )}
      {load.workflow === 'truckload' &&
        parsedWrap(
          <EditableRefNum
            defaultValue={trailerNum}
            label={`Trailer#`}
            loading={refNumsLoading}
            name="trailerNum"
            onSave={editTrailerNum.mutate}
            readOnly={!canBook}
          />,
        )}
      {load.workflow === 'truckload' &&
        parsedWrap(
          <EditableRefNum
            defaultValue={sealNum}
            label={`Seal#`}
            loading={refNumsLoading}
            name="sealNum"
            onSave={editSealNum.mutate}
            readOnly={!canBook}
          />,
        )}
      {workflow !== 'parcel' &&
        parsedWrap(
          <EditableRefNum
            defaultValue={bol?.bolNumber}
            label="BOL#"
            loading={editBOLNum.isPending}
            name="bolNum"
            onSave={editBOLNum.mutate}
            readOnly={!canBook}
            inputGroupClassName="relative"
            renderEndContentBefore={value => (
              <div className="absolute top-0 right-24 h-full flex items-center px-2 z-[9999]">
                <BolNumAvailabilityAdornment
                  bolNumber={value}
                  loadId={loadId}
                />
              </div>
            )}
          />,
        )}
      {tractorNum &&
        parsedWrap(<ReadonlyRefNum value={tractorNum} label="Tractor#" />)}
      {loadNum && parsedWrap(<ReadonlyRefNum value={loadNum} label="Load#" />)}
    </>
  )
}
